import angular from "angular";

export function registerClickOutsideDirective() {
  const ccqdirectives = angular.module("Ccq.directives");

  ccqdirectives.directive("clickOutside", [
    "$document",
    "$rootScope",
    function ($document: ng.IDocumentService, $rootScope: ng.IRootScopeService) {
      // Keep a registry of elements using the directive so we only bind one document click
      const registry = new Set<{ el: Element; handler: () => void; isOpen: () => boolean }>();
      let bound = false;

      // Function to check document clicks and notify any elements that were clicked outside
      function onDocClick(event: MouseEvent) {
        let fired = false;

        registry.forEach(({ el, handler, isOpen }) => {
          // Only act if this instance is active and the click was outside the element
          if (!isOpen() || el.contains(event.target as Node)) return;

          // Queue the consumer's handler (coalesces into a single digest)
          handler();
          fired = true;
        });

        // Trigger a digest once if any handlers fired
        if (fired && !$rootScope.$$phase) {
          $rootScope.$applyAsync();
        }
      }

      // Listen for click events on the document (bind once)
      function bindOnce() {
        if (!bound) {
          // jqLite adds one native listener; we fan out via the registry
          $document.on("click", onDocClick as any);
          bound = true;
        }
      }

      // Remove the document listener when no instances remain
      function unbindIfEmpty() {
        if (bound && registry.size === 0) {
          $document.off("click", onDocClick as any);
          bound = false;
        }
      }

      return {
        restrict: "A",
        scope: {
          // Expression to invoke when a click occurs outside the element
          clickOutside: "&",

          // Optional predicate; when provided, only active (truthy) instances respond
          clickOutsideWhen: "&?",
        },
        link(scope: any, element: any) {
          // Register this instance
          const entry = {
            el: element[0] as Element,
            handler: () => scope.$evalAsync(() => { scope.clickOutside(); }),
            isOpen: () => (scope.clickOutsideWhen ? !!scope.clickOutsideWhen() : false),
          };

          registry.add(entry);
          bindOnce();

          // Cleanup listener when the directive is destroyed
          scope.$on("$destroy", () => {
            registry.delete(entry);
            unbindIfEmpty();
          });
        },
      };
    },
  ]);
}
